import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { StaffFilterSelect, useStaffParam } from "@/components/StaffFilterSelect";
import { api, apiErr } from "@/lib/api";
import { Loader, Card, EmptyState, PageHeader } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { CheckSquare, CheckCircle2, ArrowRight, Trash2, X } from "lucide-react";
import { useStaffNames } from "@/hooks/useStaffNames";
import { ImportantTag, TaskImportantToggle } from "@/components/CaseOwnership";

const COLUMNS = ["To Do", "In Progress", "Waiting for Client", "Completed", "Cancelled"];
const ACTIVE_COLUMNS = COLUMNS.filter((c) => c !== "Completed");

export default function TasksBoard() {
  const names = useStaffNames();
  const [tasks, setTasks] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [view, setView] = useState("active"); // "active" | "completed"
  const [staffF, setStaffF] = useStaffParam();
  const [params, setParams] = useSearchParams();
  const overdueOnly = params.get("overdue") === "1";
  const clearOverdue = () => { const p = new URLSearchParams(params); p.delete("overdue"); setParams(p, { replace: true }); };
  const today = new Date().toISOString().slice(0, 10);
  const isMine = (t) => !staffF || t.assigned_staff === staffF || t.case_consultant === staffF || t.case_staff === staffF;
  const isOverdue = (t) => t.due_date && t.due_date.slice(0, 10) < today && !["Completed", "Cancelled"].includes(t.status);
  const load = () => api.get("/tasks").then((r) => setTasks(r.data)).catch(() => setTasks([]));
  useEffect(() => { load(); }, []);

  const move = async (t, status) => {
    try {
      const { data } = await api.put(`/tasks/${t.id}`, { status });
      if (data?.case_auto_closed) toast.success("All tasks on that case are done — the case was closed automatically.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  // Completed tasks live in their own folder, out of the active board.
  const [fImportant, setFImportant] = useState(false);
  const setImp = (id, v) => setTasks((xs) => xs.map((x) => (x.id === id ? { ...x, important: v } : x)));
  const visible = (tasks || []).filter((t) => (view === "completed" ? t.status === "Completed" : t.status !== "Completed") && (!fImportant || t.important) && isMine(t) && (!overdueOnly || isOverdue(t)))
    .sort((a, b) => (b.important ? 1 : 0) - (a.important ? 1 : 0));
  const cols = view === "completed" ? ["Completed"] : ACTIVE_COLUMNS;

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearSel = () => setSelected(new Set());
  const visibleIds = visible.map((t) => t.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(visibleIds));

  const switchView = (v) => { setView(v); clearSel(); };

  const deleteOne = async (t) => {
    if (!window.confirm(`Permanently delete "${t.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Task deleted");
      setSelected((prev) => { const n = new Set(prev); n.delete(t.id); return n; });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} task${ids.length > 1 ? "s" : ""}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/tasks/bulk-delete", { ids });
      toast.success(`${data.deleted} task${data.deleted === 1 ? "" : "s"} deleted`);
      clearSel();
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setDeleting(false); }
  };

  if (!tasks) return <Loader />;
  const completedCount = tasks.filter((t) => t.status === "Completed" && isMine(t)).length;
  return (
    <div>
      <PageHeader title="Tasks" subtitle="Every case task across the team, by status.">
        <StaffFilterSelect value={staffF} onChange={(v) => { setStaffF(v); clearSel(); }} myLabel="My tasks" testid="task-filter-staff" />
        {overdueOnly && <button type="button" data-testid="task-filter-overdue-clear" onClick={clearOverdue} className="px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-sm font-semibold">Overdue only ✕</button>}
        <button type="button" data-testid="task-filter-important" onClick={() => setFImportant(!fImportant)} className={`px-3 py-2 rounded-xl border text-sm font-semibold ${fImportant ? "bg-rose-600 text-white border-rose-600" : "border-slate-200 text-rose-600 bg-white"}`}>Important only</button>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1" data-testid="task-view-toggle">
          <button data-testid="task-view-active" onClick={() => switchView("active")} className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-colors ${view === "active" ? "bg-primary text-white" : "text-slate-500"}`}>Active</button>
          <button data-testid="task-view-completed" onClick={() => switchView("completed")} className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${view === "completed" ? "bg-primary text-white" : "text-slate-500"}`}><CheckCircle2 className="h-3.5 w-3.5" /> Completed ({completedCount})</button>
        </div>
        {visible.length > 0 && (
          <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 cursor-pointer select-none">
            <input type="checkbox" data-testid="task-select-all" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-rose-600 cursor-pointer" />
            Select all ({visible.length})
          </label>
        )}
      </div>

      {selected.size > 0 && (
        <div data-testid="task-bulk-bar" className="sticky top-2 z-20 mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white/90 backdrop-blur px-4 py-3 shadow-sm">
          <span className="text-sm font-semibold text-slate-700" data-testid="task-bulk-count">{selected.size} selected</span>
          <button data-testid="task-bulk-delete" onClick={deleteSelected} disabled={deleting} className="inline-flex items-center gap-1.5 bg-rose-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-rose-700 transition-colors disabled:opacity-60">
            <Trash2 className="h-4 w-4" /> {deleting ? "Deleting..." : "Delete selected"}
          </button>
          <button data-testid="task-bulk-clear" onClick={clearSel} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
            <X className="h-4 w-4" /> Clear
          </button>
        </div>
      )}

      {visible.length === 0 ? (
        <Card className="p-6"><EmptyState icon={view === "completed" ? CheckCircle2 : CheckSquare} title={view === "completed" ? "No completed tasks" : "No active tasks"} description={view === "completed" ? "Tasks you mark Completed move here, out of the active board." : "Tasks created on cases will appear here."} /></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {cols.map((col) => {
            const items = visible.filter((t) => t.status === col);
            if (items.length === 0) return null;
            const colIds = items.map((t) => t.id);
            const colAllSelected = colIds.every((id) => selected.has(id));
            const toggleCol = () => setSelected((prev) => {
              const next = new Set(prev);
              colAllSelected ? colIds.forEach((id) => next.delete(id)) : colIds.forEach((id) => next.add(id));
              return next;
            });
            return (
              <div key={col}>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <input type="checkbox" data-testid={`task-select-col-${col}`} checked={colAllSelected} onChange={toggleCol} className="h-4 w-4 accent-rose-600 cursor-pointer" aria-label={`Select all ${col} tasks`} />
                  <StatusBadge status={col} /><span className="text-xs text-slate-400">{items.length}</span>
                </div>
                <div className="space-y-2">
                  {items.map((t) => {
                    const isSel = selected.has(t.id);
                    return (
                      <Card key={t.id} data-testid={`task-${t.id}`} className={`p-4 transition-shadow ${t.important ? "border-l-4 border-l-rose-500" : ""} ${isSel ? "ring-2 ring-rose-400" : ""}`}>
                        <div className="flex items-start gap-2.5">
                          <input type="checkbox" data-testid={`task-select-${t.id}`} checked={isSel} onChange={() => toggle(t.id)} className="mt-1 h-4 w-4 shrink-0 accent-rose-600 cursor-pointer" aria-label={`Select ${t.title}`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-slate-900">{t.title}</span><StatusBadge status={t.priority} />{t.important && <ImportantTag />}<span className="ml-auto"><TaskImportantToggle task={t} onChange={(v) => setImp(t.id, v)} /></span></div>
                            <div className="text-xs text-slate-500 mt-1">{t.client_name} · <span className="font-mono">{t.app_number}</span></div>
                            {t.due_date && <div className={`text-xs mt-0.5 ${isOverdue(t) ? "text-rose-600 font-semibold" : "text-slate-400"}`}>{isOverdue(t) ? "Overdue · " : ""}Due {t.due_date}</div>}
                            <div className="text-[11px] text-slate-400 mt-0.5" data-testid={`task-people-${t.id}`}>Assigned to {names[t.assigned_staff] || "—"} · Created by {t.created_by_name || "—"}</div>
                            <div className="flex items-center justify-between gap-2 mt-3">
                              <select value={t.status} onChange={(e) => move(t, e.target.value)} data-testid={`task-status-${t.id}`} className="px-2 py-1 rounded-lg border border-slate-200 text-xs font-medium">{COLUMNS.map((s) => <option key={s}>{s}</option>)}</select>
                              <div className="flex items-center gap-2">
                                {t.application_id && <Link to={`/admin/applications/${t.application_id}`} className="text-xs font-semibold text-primary inline-flex items-center gap-1">Case <ArrowRight className="h-3 w-3" /></Link>}
                                <button data-testid={`task-delete-${t.id}`} onClick={() => deleteOne(t)} title="Delete task permanently" className="text-slate-400 hover:text-rose-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
